import isEqual from 'lodash/isEqual';
import { useEffect, useRef } from 'react';
import { useInstantSearch } from 'react-instantsearch';

export type SearchIndexStateApplierProps = {
  expectedIndexState: Record<string, unknown>;
  indexId: string;
  onStateApplied?: () => void;
};

/**
 * This component applies UI state to a nested Index AFTER widgets have mounted.
 * Unlike InstantSearchStateUpdater which skips initial mount (relying on SSR),
 * this component is specifically for SearchView where serverState is null and
 * we need to apply state on initial mount once widgets are ready.
 *
 * It watches the search status and applies state when the search is ready,
 * which indicates widgets have been registered.
 *
 * It must be placed INSIDE the nested <Index> component, after the widgets.
 */
export const SearchIndexStateApplier = ({
  expectedIndexState,
  indexId,
  onStateApplied,
}: SearchIndexStateApplierProps) => {
  const { setUiState, uiState, status } = useInstantSearch();
  const hasApplied = useRef(false);
  const attemptCount = useRef(0);
  // Track when we're waiting for the filtered search to complete
  const waitingForSearchComplete = useRef(false);

  useEffect(() => {
    // If we've applied state and are waiting for the search to complete
    if (waitingForSearchComplete.current && status === 'idle') {
      waitingForSearchComplete.current = false;
      onStateApplied?.();
      return;
    }

    // Only apply once
    if (hasApplied.current) {
      return;
    }

    // Check if we have expected state to apply (excluding query which is at root level)
    const hasExpectedState =
      expectedIndexState &&
      Object.keys(expectedIndexState).some(
        (key) =>
          key !== 'query' &&
          expectedIndexState[key] &&
          (Array.isArray(expectedIndexState[key])
            ? (expectedIndexState[key] as unknown[]).length > 0
            : typeof expectedIndexState[key] === 'object'
              ? Object.keys(expectedIndexState[key] as object).length > 0
              : true),
      );

    if (!hasExpectedState) {
      hasApplied.current = true;
      onStateApplied?.();
      return;
    }

    // Check if current state already matches
    const currentIndexState = uiState[indexId] || {};
    if (isEqual(currentIndexState, expectedIndexState)) {
      hasApplied.current = true;
      onStateApplied?.();
      return;
    }

    // Wait for search to be ready (status 'idle' indicates widgets are registered)
    // Also need to wait for ListingFilters to render (it returns null on first render)
    // Use a retry approach to ensure we catch when widgets are ready
    attemptCount.current++;

    // Only apply after status becomes 'idle' or 'loading' (not during first mount)
    if (status === 'idle' || status === 'loading') {
      const timeoutId = setTimeout(() => {
        if (hasApplied.current) {
          return;
        }

        // Apply the expected state
        hasApplied.current = true;
        setUiState((prevState) => ({
          ...prevState,
          [indexId]: {
            ...prevState[indexId],
            ...expectedIndexState,
          },
        }));
        // Wait for the search to complete before signaling ready
        // This ensures the filtered results are loaded before showing counts
        waitingForSearchComplete.current = true;
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [
    expectedIndexState,
    indexId,
    setUiState,
    uiState,
    onStateApplied,
    status,
  ]);

  return null;
};
